// Shared shapes for the validation schema files. A validator receives the form
// `values` bag (+ the subscription snapshot) and returns `true`/`false` for a
// pass or an error object keyed by control name.
export type ValidationError = Record<string, unknown>;
export type ValidationResult = boolean | ValidationError;
export type Validation<V = Record<string, unknown>> = (
  values: V,
  subscriptions: Subscriptions,
) => ValidationResult;

function isNullOrUndefined(value: unknown): boolean {
  return (value !== null) && (value !== undefined);
}

function isRequired(value: unknown): boolean {
  return isNullOrUndefined(value) && (value as string).trim().length > 0;
}

function isSame(value1: unknown, value2: unknown): boolean {
  return value1 === value2;
}

function isNumber(value: unknown): boolean {
  return isNullOrUndefined(value) && (value !== '') && !Number.isNaN(Number(value));
}

function isNumberString(value: unknown): boolean {
  return typeof value === 'string' && (value === Number(value).toString());
}

function isArraySizeAtLeast(value: unknown, size: number): boolean {
  return Array.isArray(value) && (value.length >= size);
}

function isNonEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function isUniqArray(value: unknown): boolean {
  return Array.isArray(value) && (value.length === new Set(value).size);
}

function isNumberArray(value: unknown): boolean {
  return Array.isArray(value) && value.every((v) => typeof v === 'number');
}

function isStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && !!value;
}

function isDate(value: unknown): boolean {
  return value instanceof Date && !Number.isNaN(new Date(value).getTime());
}

function isDateLoose(value: unknown): boolean {
  if (!value) {
    return false;
  }
  return !Number.isNaN(new Date(value as string | number | Date).getTime());
}

function isISODate(value: unknown): boolean {
  return (new Date(value as string | number | Date).toISOString() === value);
}

function isEmail(value: unknown, required?: unknown): boolean {
  if (!required) {
    return true;
  }
  return /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(String(value));
}

function isPhoneNumber(value: unknown, required?: unknown): boolean {
  if (!required) {
    return true;
  }
  return /^[0-9()]{6,10}$/.test(String(value));
}

export {
  isNullOrUndefined,
  isRequired,
  isSame,
  isNumber,
  isNumberString,
  isNonEmptyArray,
  isArraySizeAtLeast,
  isUniqArray,
  isNumberArray,
  isStringArray,
  isNonEmptyString,
  isDate,
  isDateLoose,
  isISODate,
  isEmail,
  isPhoneNumber,
};
